'use client';

import { datadogLogs } from '@datadog/browser-logs';
import type { LogRecord } from '@logtape/logtape';

import { EnvironmentNameEnum, getEnvironmentName } from '@/config/environment';

let initialized = false;
const DATADOG_TOKEN = 'puba1ad5273bdb658063531500b18b763f8';

const PII_PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
  [/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[REDACTED_EMAIL]'],
  [/\b[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g, '[REDACTED_TOKEN]'],
  [/((?:token|access_token|password|pin)=)[^&#\s'"]+/gi, '$1[REDACTED]'],
  [/\b\d{3}\.\d{3}\.\d{3}-\d{2}\b/g, '[REDACTED_CPF]'],
];

function redactPII(value: string): string {
  return PII_PATTERNS.reduce(
    (redacted, [pattern, replacement]) => redacted.replace(pattern, replacement),
    value,
  );
}

const DATADOG_LEVELS: Record<string, 'debug' | 'info' | 'warn' | 'error'> = {
  trace: 'debug',
  debug: 'debug',
  info: 'info',
  warning: 'warn',
  error: 'error',
  fatal: 'error',
};

function initDatadog() {
  if (initialized || typeof window === 'undefined') {
    return;
  }

  const environmentName = getEnvironmentName();

  if (environmentName === EnvironmentNameEnum.LOCAL) {
    return;
  }

  initialized = true;

  datadogLogs.init({
    clientToken: DATADOG_TOKEN,
    site: 'datadoghq.com',
    service: 'medprev-web-app-frontend',
    env: environmentName,
    version: process.env.NEXT_PUBLIC_GIT_COMMIT_SHA,
    sessionSampleRate: 100,
    forwardErrorsToLogs: true,
    beforeSend: (log) => {
      log.message = redactPII(log.message);

      if (log.error?.message) {
        log.error.message = redactPII(log.error.message);
      }

      if (log.error?.stack) {
        log.error.stack = redactPII(log.error.stack);
      }

      if (log.view?.url) {
        log.view.url = redactPII(log.view.url);
      }

      return true;
    },
  });
}

export function getDatadogClientSink() {
  if (typeof window !== 'undefined') {
    setTimeout(initDatadog, 0);
  }

  return (record: LogRecord) => {
    if (!initialized) {
      return;
    }

    const level = DATADOG_LEVELS[record.level] ?? 'info';
    const [, ...emittingModule] = record.category;
    const context = emittingModule.join('.');

    datadogLogs.logger[level]?.(
      record.message.join(''),
      context ? { ...record.properties, context } : { ...record.properties },
    );
  };
}
